import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { adminLogin, setAdminCsrfToken, setAdminToken } from "../adminApi";

const ADMIN_TOKEN_WARNING_SECONDS = 5 * 60;

let memoryToken = null;

function readStoredToken() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      return window.localStorage.getItem("offlico.admin.token");
    } catch {
      return null;
    }
  }
  return memoryToken;
}

function writeStoredToken(value) {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.setItem("offlico.admin.token", value);
      return;
    } catch {
      // ignore
    }
  }
  memoryToken = value;
}

function clearStoredToken() {
  if (typeof window !== "undefined" && window.localStorage) {
    try {
      window.localStorage.removeItem("offlico.admin.token");
      return;
    } catch {
      // ignore
    }
  }
  memoryToken = null;
}

function decodeBase64Url(input) {
  const normalized = String(input || "")
    .replace(/-/g, "+")
    .replace(/_/g, "/")
    .replace(/\s/g, "");
  const padLength = (4 - (normalized.length % 4)) % 4;
  const padded = normalized + "=".repeat(padLength);
  try {
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function readAdminTokenExpiryAt(token) {
  if (!token || typeof atob !== "function") return null;
  const parts = String(token).split(".");
  if (parts.length < 2) return null;
  const payload = decodeBase64Url(parts[1]);
  if (!payload || typeof payload.exp !== "number") return null;
  const epochMs = payload.exp * 1000;
  if (!Number.isFinite(epochMs)) return null;
  return epochMs;
}

const AdminAuthContext = createContext({
  status: "loading",
  token: null,
  role: null,
  adminId: null,
  sessionWarning: false,
  sessionSecondsRemaining: null,
  sessionExpiresAt: null,
  signIn: async () => {},
  signOut: () => {},
});

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => readStoredToken());
  const [role, setRole] = useState(null);
  const [adminId, setAdminId] = useState(null);
  const [status, setStatus] = useState("ready");
  const [sessionExpiresAt, setSessionExpiresAt] = useState(() => readAdminTokenExpiryAt(readStoredToken()));
  const [sessionWarning, setSessionWarning] = useState(false);
  const [sessionSecondsRemaining, setSessionSecondsRemaining] = useState(() => {
    const expiresAt = readAdminTokenExpiryAt(readStoredToken());
    if (!expiresAt) return null;
    return Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
  });

  const performSignOut = () => {
    clearStoredToken();
    setAdminToken(null);
    setAdminCsrfToken(null);
    setToken(null);
    setRole(null);
    setAdminId(null);
    setStatus("ready");
  };

  useEffect(() => {
    if (!token) {
      setAdminToken(null);
      setSessionExpiresAt(null);
      setSessionSecondsRemaining(null);
      setSessionWarning(false);
      setStatus("ready");
      return;
    }
    setAdminToken(token);
    const expiresAt = readAdminTokenExpiryAt(token);
    setSessionExpiresAt(expiresAt);
    if (!expiresAt) {
      setSessionWarning(false);
      setSessionSecondsRemaining(null);
    } else {
      const updateTimer = () => {
        const remainingMs = expiresAt - Date.now();
        if (remainingMs <= 0) {
          setSessionSecondsRemaining(0);
          setSessionWarning(false);
          performSignOut();
          return;
        }
        const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));
        setSessionSecondsRemaining(remainingSeconds);
        setSessionWarning(remainingSeconds <= ADMIN_TOKEN_WARNING_SECONDS && remainingSeconds > 0);
      };
      updateTimer();
      const id = setInterval(updateTimer, 1000);
      return () => clearInterval(id);
    }
    setStatus("ready");
  }, [token]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const handleInvalid = () => performSignOut();
    const handleStorageEvent = (event) => {
      if (event.key !== "offlico.admin.token") return;
      const nextToken = event.newValue;
      if (!nextToken) {
        if (token) {
          performSignOut();
        }
        return;
      }
      if (nextToken !== token) {
        setToken(nextToken);
      }
    };
    window.addEventListener("admin:invalid", handleInvalid);
    window.addEventListener("storage", handleStorageEvent);
    return () => {
      window.removeEventListener("admin:invalid", handleInvalid);
      window.removeEventListener("storage", handleStorageEvent);
    };
  }, [token]);

    const value = useMemo(
      () => ({
      status,
      token,
      role,
      adminId,
      sessionWarning,
      sessionSecondsRemaining,
      sessionExpiresAt,
      async signIn(credentials) {
        const res = await adminLogin(credentials);
        setAdminToken(res.access_token);
        setAdminCsrfToken(res.csrf_token || null);
        writeStoredToken(res.access_token);
        setToken(res.access_token);
        setRole(res.role);
        setAdminId(res.admin_id);
        setStatus("ready");
        return res;
      },
      signOut() {
        performSignOut();
      },
    }),
    [adminId, role, status, token, sessionWarning, sessionExpiresAt, sessionSecondsRemaining]
  );

  return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
}

export function useAdminAuth() {
  return useContext(AdminAuthContext);
}

export function RequireAdminAuth({ children }) {
  const { token, status } = useAdminAuth();
  const location = useLocation();
  if (status === "loading") {
    return <div className="adminGate">Checking admin session…</div>;
  }
  if (!token) {
    return <Navigate to="/admin/login" state={{ from: location.pathname }} replace />;
  }
  return children;
}
